import { hash } from '@ember/helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import type { WithBoundArgs } from '@glint/template';

import PrismButtonGroupButton from './button-group/button.gts';

interface PrismButtonGroupSignature {
  Args: {
    isDisabled?: boolean;
    isSmall?: boolean;
    onChange?: (value: string | string[]) => void;
    value: string | string[];
  };
  Blocks: {
    default: [
      {
        button: WithBoundArgs<
          typeof PrismButtonGroupButton,
          'checkedValue' | 'isDisabled' | 'isSmall' | 'onChange'
        >;
      },
    ];
  };
  Element: HTMLDivElement;
}

export default class PrismButtonGroup extends Component<PrismButtonGroupSignature> {
  get isCheckboxMode(): boolean {
    const { value } = this.args;

    return Array.isArray(value);
  }

  private handleCheckboxChange(selectedValue: string): void {
    const oldValue = this.args.value as string[];
    const { onChange } = this.args;

    if (!onChange) {
      return;
    }

    if (oldValue.includes(selectedValue)) {
      onChange(oldValue.filter((element) => element !== selectedValue));
    } else {
      onChange([...oldValue, selectedValue]);
    }
  }

  private handleRadioChange(selectedValue: string): void {
    const oldValue = this.args.value as string;
    const { onChange } = this.args;

    if (!onChange) {
      return;
    }

    if (selectedValue !== oldValue) {
      onChange(selectedValue);
    }
  }

  @action onChange(selectedValue: string): void {
    if (this.isCheckboxMode) {
      this.handleCheckboxChange(selectedValue);
    } else {
      this.handleRadioChange(selectedValue);
    }
  }

  <template>
    <div
      class="prism-button-group
        {{if this.isCheckboxMode 'prism-button-group--checkbox'}}"
      data-test-id="prism-button-group"
      role="group"
      ...attributes
    >
      {{yield
        (hash
          button=(component
            PrismButtonGroupButton
            checkedValue=@value
            isDisabled=@isDisabled
            isSmall=@isSmall
            onChange=this.onChange
          )
        )
      }}
    </div>
  </template>
}
